/**
 * Channels Query Services
 * Contains all read operations (queries) for channels
 */

import { useQuery } from '@tanstack/react-query';
import { useApi } from '../../../services/useApi';
import {
  ChannelType,
  ChannelName,
  IApiResponse,
  IGetChannelsResult,
  IApiChannel,
  IGetListingsResult,
  IApiListing,
  IChannelUnitsForImportResult,
  IApiChannelUnit,
  IGetAirbnbUnitsResult,
  IGetBookingUnitsResult,
  IGetGathernUnitsResult,
  IChannelUnitForImport,
  IGetChannelsResponse,
} from '../types';

// ============================================
// API Endpoints
// ============================================

const API_ENDPOINTS = {
  GET_CHANNELS: '/api/DistributionManagement/GetChannels',
  GET_CHANNEL_BY_TYPE: '/api/DistributionManagement/GetChannelByType',
  GET_LISTINGS: '/api/DistributionManagement/GetListings',
  GET_AIRBNB_UNITS: '/api/Mapping/GetAirbnbUnits',
  GET_BOOKING_UNITS: '/api/Mapping/GetBookingUnits',
  GET_GATHERN_UNITS: '/api/Mapping/GetGathernUnits',
} as const;

// ============================================
// Mappers
// ============================================

/** Maps ChannelType enum to ChannelName string */
export const mapChannelTypeToName = (channelType: ChannelType): ChannelName => {
  switch (channelType) {
    case ChannelType.Airbnb:
      return 'airbnb';
    case ChannelType.Booking:
      return 'booking';
    case ChannelType.Gathern:
      return 'gathern';
    default:
      return 'airbnb';
  }
};

/** Maps ChannelName string to ChannelType enum */
export const mapChannelNameToType = (channelName: ChannelName): ChannelType => {
  switch (channelName) {
    case 'airbnb':
      return ChannelType.Airbnb;
    case 'booking':
      return ChannelType.Booking;
    case 'gathern':
      return ChannelType.Gathern;
    default:
      return ChannelType.Airbnb;
  }
};


// ============================================
// Query Hooks
// ============================================

/**
 * Hook to fetch all channels
 * @returns Query result with channels data directly from API
 */
export const useChannelsQuery = () => {
  const { apiPrivate } = useApi();

  const fetchChannels = async (): Promise<IGetChannelsResponse> => {
    const response = await apiPrivate.get<IApiResponse<IGetChannelsResult>>(
      API_ENDPOINTS.GET_CHANNELS
    );

    if (!response.data.isSuccess) {
      throw new Error(
        response.data.errorMessages.join(', ') || 'Failed to fetch channels'
      );
    }

    // Return API response directly - no mapping
    return {
      channels: response.data.result?.channels || [],
    };
  };

  return useQuery({
    queryKey: ['channels'],
    queryFn: fetchChannels,
    staleTime: 0,
    retry: 2,
  });
};

/**
 * Hook to fetch a single channel by type using the dedicated API endpoint
 * @param channelName - The channel name (airbnb, booking, gathern)
 * @returns Query result with channel data directly from API
 */
export const useChannelByTypeQuery = (channelName: ChannelName) => {
  const { apiPrivate } = useApi();

  const fetchChannelByType = async (): Promise<IApiChannel | null> => {
    const channelType = mapChannelNameToType(channelName);
    
    const response = await apiPrivate.get<IApiResponse<IApiChannel>>(
      API_ENDPOINTS.GET_CHANNEL_BY_TYPE,
      {
        params: {
          channelType,
        },
      }
    );

    if (!response.data.isSuccess) {
      throw new Error(
        response.data.errorMessages.join(', ') || 'Failed to fetch channel'
      );
    }

    // Return API response directly - no mapping
    return response.data.result;
  };

  return useQuery({
    queryKey: ['channel-by-type', channelName],
    queryFn: fetchChannelByType,
    staleTime: 0,
    retry: 2,
    enabled: !!channelName,
  });
};

/**
 * Hook to fetch a single channel by name
 * @deprecated Use useChannelByTypeQuery instead for better performance
 * @param channelName - The channel name (airbnb, booking, gathern)
 * @returns Query result with channel data directly from API
 */
export const useChannelQuery = (channelName: ChannelName) => {
  const { apiPrivate } = useApi();

  const fetchChannelByName = async (): Promise<IApiChannel | null> => {
    const response = await apiPrivate.get<IApiResponse<IGetChannelsResult>>(
      API_ENDPOINTS.GET_CHANNELS
    );

    if (!response.data.isSuccess) {
      throw new Error(
        response.data.errorMessages.join(', ') || 'Failed to fetch channels'
      );
    }

    // Find channel by type - no mapping
    const channelType = mapChannelNameToType(channelName);
    const channels = response.data.result?.channels || [];
    const channel = channels.find((c: IApiChannel) => c.channelType === channelType);
    return channel || null;
  };

  return useQuery({
    queryKey: ['channel', channelName],
    queryFn: fetchChannelByName,
    staleTime: 0,
    retry: 2,
    enabled: !!channelName,
  });
};

/**
 * Hook to fetch units/listings available for import from a channel
 * @param channelName - The channel name (airbnb, booking, gathern)
 * @param channelId - The channel ID (required for Airbnb)
 * @param accountId - The account ID to ensure unique queries per account
 * @returns Query result with listings data including userChannelId and channelType for import
 */
export const useChannelUnitsForImportQuery = (
  channelName: ChannelName,
  channelId?: string | null,
  accountId?: string | null
) => {
  const { apiPrivate } = useApi();

  const fetchListings = async (): Promise<IChannelUnitsForImportResult> => {
    const channelType = mapChannelNameToType(channelName);
    
    const response = await apiPrivate.get<IApiResponse<IGetListingsResult>>(
      API_ENDPOINTS.GET_LISTINGS,
      {
        params: {
          channelId: channelId,
          channelType: channelType,
        },
      }
    );

    if (!response.data.isSuccess) {
      throw new Error(
        response.data.errorMessages?.join(', ') || 'Failed to fetch listings'
      );
    }

    let listings: IApiListing[] = [];
    let userChannelId: number | null = null;
    let resultChannelType: ChannelType | null = null;
    
    if (response.data.result) {
      if (typeof response.data.result === 'object' && !Array.isArray(response.data.result)) {
        const result = response.data.result as IGetListingsResult;
        
        if (result.success === false) {
          const error = new Error(result.errorMessage || 'Failed to fetch listings') as Error & { 
            errorMessageAr?: string;
          };
          error.errorMessageAr = result.errorMessageAr ?? undefined;
          throw error;
        }
        
        userChannelId = result.userChannelId ?? null;
        resultChannelType = result.channelType ?? null;
        
        if (result.listings && Array.isArray(result.listings)) {
          listings = result.listings;
        }
      } else if (Array.isArray(response.data.result)) {
        listings = response.data.result;
      }
    }

    const units: IChannelUnitForImport[] = listings.map((listing, index) => ({
      id: listing.listingId || `listing-${index}`,
      name: listing.listingName || '',
      // Handle both 'location' and 'address' fields from API
      address: listing.location || '',
      imageUrl: listing.imageUrl ?? undefined,
      propertyId: listing.listingId || '',
      isSynced: listing.isConnected ?? false,
      availableActions: listing.availableActions ?? [],
      syncStatus: listing.syncStatus,
      syncStatusName: listing.syncStatusName,
      syncStatusNameAr: listing.syncStatusNameAr,
    }));

    return {
      units,
      userChannelId,
      channelType: resultChannelType,
    };
  };

  return useQuery({
    queryKey: ['channel-units-import', channelName, channelId, accountId],
    queryFn: fetchListings,
    staleTime: 0,
    retry: 2,
    enabled: !!channelName && !!channelId,
  });
};

/**
 * Hook to fetch Airbnb units from the dedicated API endpoint
 * @returns Query result with Airbnb units data
 */
export const useGetAirbnbUnitsQuery = () => {
  const { apiPrivate } = useApi();

  const fetchAirbnbUnits = async (): Promise<IGetAirbnbUnitsResult> => {
    const response = await apiPrivate.get<IApiResponse<IApiChannelUnit[]>>(
      API_ENDPOINTS.GET_AIRBNB_UNITS
    );

    if (!response.data.isSuccess) {
      throw new Error(
        response.data.errorMessages?.join(', ') || 'Failed to fetch Airbnb units'
      );
    }

    return {
      units: response.data.result || [],
    };
  };

  return useQuery({
    queryKey: ['airbnb-units'],
    queryFn: fetchAirbnbUnits,
    staleTime: 0,
    retry: 2,
  });
};

/**
 * Hook to fetch Booking units from the dedicated API endpoint
 * @returns Query result with Booking units data
 */
export const useGetBookingUnitsQuery = () => {
  const { apiPrivate } = useApi();

  const fetchBookingUnits = async (): Promise<IGetBookingUnitsResult> => {
    const response = await apiPrivate.get<IApiResponse<IApiChannelUnit[]>>(
      API_ENDPOINTS.GET_BOOKING_UNITS
    );

    if (!response.data.isSuccess) {
      throw new Error(
        response.data.errorMessages?.join(', ') || 'Failed to fetch Booking units'
      );
    }

    return {
      units: response.data.result || [],
    };
  };

  return useQuery({
    queryKey: ['booking-units'],
    queryFn: fetchBookingUnits,
    staleTime: 0,
    retry: 2,
  });
};

/**
 * Hook to fetch Gathern units from the dedicated API endpoint
 * @returns Query result with Gathern units data
 */
export const useGetGathernUnitsQuery = () => {
  const { apiPrivate } = useApi();

  const fetchGathernUnits = async (): Promise<IGetGathernUnitsResult> => {
    const response = await apiPrivate.get<IApiResponse<IApiChannelUnit[]>>(
      API_ENDPOINTS.GET_GATHERN_UNITS
    );

    if (!response.data.isSuccess) {
      throw new Error(
        response.data.errorMessages?.join(', ') || 'Failed to fetch Gathern units'
      );
    }

    return {
      units: response.data.result || [],
    };
  };

  return useQuery({
    queryKey: ['gathern-units'],
    queryFn: fetchGathernUnits,
    staleTime: 0,
    retry: 2,
  });
};
